import { patientIdSchema } from "@repo/api/schemas";
import {
  useCases,
  useConditions,
  useNotes,
  usePatient,
  useQuestionnaireInstances,
  useSymptoms,
  useVisits,
} from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { openOverlay } from "@/app/overlays";
import { noteEntries } from "@/features/notes";
import {
  NoteList,
  PatientProblemList,
  PatientSummaryBanner,
  SymptomList,
  sortByDateDesc,
  sortByEffectiveDesc,
} from "@/features/patients";

/** The overview shows the latest few; the Notes tab has the rest. */
const LATEST_NOTES = 5;

const INVALID_VERIFICATION: ReadonlySet<string> = new Set(["refuted", "entered-in-error"]);

export const Route = createFileRoute("/patients/$patientId/_layout/")({
  component: RouteComponent,
});

function RouteComponent() {
  const patientId = patientIdSchema.parse(Route.useParams().patientId);
  const navigate = useNavigate();
  const { patient } = usePatient(patientId);
  const { conditions } = useConditions({ patientId });
  const cases = useCases({ patientId });
  const responses = useQuestionnaireInstances({ patientId });
  const { visits } = useVisits({ patientId });
  const { notes } = useNotes({ patientId });
  const { symptoms } = useSymptoms({ patientId });

  const problemListConditions = useMemo(
    () =>
      conditions.filter(
        (c) => c.category === "problem-list-item" && !INVALID_VERIFICATION.has(c.verificationStatus ?? ""),
      ),
    [conditions],
  );

  const conditionToCaseId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cases) for (const id of c.diagnosis ?? []) if (!m.has(id)) m.set(id, c.caseId);
    return m;
  }, [cases]);

  /** Visit id → case id, used to attribute a note to its case. */
  const visitToCaseId = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of visits) if (v.episodeOfCareId) m.set(v.visitId, v.episodeOfCareId);
    return m;
  }, [visits]);

  const sortedNotes = useMemo(() => sortByDateDesc(notes), [notes]);
  const entries = useMemo(() => noteEntries(notes), [notes]);
  const latestNotes = entries.slice(0, LATEST_NOTES);
  const visitByNote = useMemo(
    () => new Map(notes.flatMap((n) => (n.visitId ? [[n.noteId, n.visitId] as const] : []))),
    [notes],
  );
  const sortedSymptoms = useMemo(() => sortByEffectiveDesc(symptoms), [symptoms]);

  // Banner summary: latest note status drives the status badge.
  const noteStatus = useMemo(() => {
    const latest = sortedNotes[0];
    return latest?.status === "in-progress" || latest?.status === "completed" ? latest.status : undefined;
  }, [sortedNotes]);

  const handleOpenWorkbench = useCallback(
    () => navigate({ to: "/patients/$patientId/workbench", params: { patientId } }),
    [navigate, patientId],
  );

  const handleAddCondition = useCallback(() => openOverlay("patient.condition", { patientId }), [patientId]);
  const handleAddSymptom = useCallback(
    () => openOverlay("patient.symptom", { patientId, preselectedConditionId: null }),
    [patientId],
  );

  if (!patient) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_400px]">
      <div className="min-w-0 space-y-4">
        <PatientSummaryBanner
          diagnoses={problemListConditions.length}
          symptoms={sortedSymptoms.length}
          assessments={responses.length}
          noteStatus={noteStatus}
          onOpen={handleOpenWorkbench}
        />
      </div>
      <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
        <PatientProblemList
          conditions={problemListConditions}
          patientId={patientId}
          conditionToCaseId={conditionToCaseId}
          onAddCondition={handleAddCondition}
        />
        <SymptomList>
          <SymptomList.Header
            action={
              <Button
                variant="ghost"
                size="icon"
                className="-mr-1 h-6 w-6"
                onClick={handleAddSymptom}
                title="Record symptom"
              >
                <Plus className="h-4 w-4" />
              </Button>
            }
          />
          <SymptomList.Body>
            {sortedSymptoms.length === 0 && <SymptomList.Empty />}
            {sortedSymptoms.map((s) => (
              <SymptomList.Item key={s.observationId} symptom={s} />
            ))}
          </SymptomList.Body>
        </SymptomList>
        <NoteList>
          <NoteList.Header />
          <NoteList.Body>
            {latestNotes.length === 0 && <NoteList.Empty />}
            {latestNotes.map((entry) => {
              const visitId = visitByNote.get(entry.noteId);
              const caseId = visitId ? visitToCaseId.get(visitId) : undefined;
              return (
                <NoteList.Item
                  key={entry.noteId}
                  entry={entry}
                  linkToCase={caseId ? { patientId, caseId } : undefined}
                />
              );
            })}
            {entries.length > latestNotes.length && (
              <Link
                to="/patients/$patientId/notes"
                params={{ patientId }}
                className="block text-xs text-muted-foreground hover:text-foreground"
              >
                All {entries.length} notes
              </Link>
            )}
          </NoteList.Body>
        </NoteList>
      </aside>
    </div>
  );
}
